import * as CSS from 'csstype';

interface CreateAriaHiderOptions {
  selector: string;
  preventScroll?: boolean;
}

const DEFAULT_OPTIONS: CreateAriaHiderOptions = {
  selector: 'body > :not(script)',
  preventScroll: true,
};

// eslint-disable-next-line @typescript-eslint/no-empty-function
const noop = () => {};

/**
 * Adds `aria-hidden` attribute to all nodes except the container node and its shadow root.
 * @param containerNode the container node.
 * @param options.selector the selector to find the root nodes.
 * @param options.preventScroll whether to prevent scrolling.
 * @returns a function to restore the `aria-hidden` attributes and scroll state.
 */
export function createAriaHider<T extends HTMLElement = HTMLDivElement>(
  containerNode: T,
  options: CreateAriaHiderOptions = DEFAULT_OPTIONS,
) {
  const ATTRIBUTE = 'aria-hidden';

  const scrollPreventer = options.preventScroll ? createScrollPreventer() : noop;

  const rootNodes = getRootNodes(containerNode, options.selector, ATTRIBUTE);

  return () => {
    requestAnimationFrame(() => {
      rootNodes.forEach((item) => {
        if (!item) {
          return;
        }

        if (item.ariaHidden === null) {
          item.node.removeAttribute(ATTRIBUTE);
        } else {
          item.node.setAttribute(ATTRIBUTE, item.ariaHidden);
        }

        scrollPreventer();
      });
    });
  };
}

/**
 * Prevents scrolling on the body element.
 * @returns a cleanup function to restore the scroll state.
 */
function createScrollPreventer<T extends HTMLElement = HTMLDivElement>() {
  const scrollBarWidth = `${getScrollBarWidth()}px`;

  const styles: CSS.PropertiesHyphen = {
    overflow: 'hidden',
    'overscroll-behavior': 'contain',
    position: 'relative',
    'margin-right': scrollBarWidth,
  };

  // Get all fixed headers and navs.
  const extras = Array.from<T>(document.querySelectorAll('header, nav')).filter((el) => {
    return getComputedStyle(el).position === 'fixed';
  });

  requestAnimationFrame(() => {
    Object.entries(styles).forEach(([key, value]) => {
      document.body.style.setProperty(key, value, 'important');
    });

    // To prevent flickering when removing the scrollbar, add padding to the fixed elements.
    extras.forEach((el) => {
      el.style.setProperty('padding-right', scrollBarWidth, 'important');
    });
  });

  return () => {
    requestAnimationFrame(() => {
      Object.entries(styles).forEach(([key]) => {
        document.body.style.removeProperty(key);
      });

      extras.forEach((el) => {
        el.style.removeProperty('padding-right');
      });
    });
  };
}

/**
 * Calculates the width of the scrollbar.
 * @returns the width of the scrollbar.
 */
function getScrollBarWidth() {
  const tempElement = document.createElement('div');

  tempElement.style.cssText = 'overflow:scroll; visibility:hidden; position:absolute;';

  document.body.appendChild(tempElement);
  const width = tempElement.offsetWidth - tempElement.clientWidth;
  tempElement.remove();
  return width;
}

/**
 * Gets the root nodes in a container.
 * Will add `attribute` attribute to all nodes except the container node and its shadow root.
 * @param containerNode the container node.
 * @param selector the selector to find the root nodes.
 * @param attribute the attribute to add to the root nodes.
 * @returns an array of objects containing the element and the attribute.
 */
function getRootNodes<T extends HTMLElement = HTMLDivElement>(containerNode: T, selector: string, attribute: string) {
  return Array.from<T>(document.querySelectorAll(selector)).map((node) => {
    if (node?.shadowRoot?.contains(containerNode) || node.contains(containerNode)) {
      return undefined;
    }

    const ariaHidden = node.getAttribute(attribute);

    if (ariaHidden === null || ariaHidden === 'false') {
      requestAnimationFrame(() => {
        node.setAttribute(attribute, 'true');
      });
    }

    return { node, ariaHidden };
  });
}
